import { useActions } from 'ActionsContext';
import { CommonLoggedInDataContext } from 'CommonLoggedInData';
import { Permissions } from 'Permission';
import PermissionSidebarItem from 'PermissionSidebarItem';
import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AbstractSidebarBase from '../AbstractSidebar';
import NotificationBubble from '../NotificationBubble';
import SidebarItem from '../Sidebar/SidebarItem';
import JobStatusSidebarItem from './JobStatusSidebarItem';
import SettingsSidebarItem from './SettingsSidebarItem';

class UserSidebarBase extends AbstractSidebarBase {
  sidebarItems(): ReturnType<typeof SidebarItem>[] {
    const { location, mobile } = this.props;
    const expandDueToChildren = this.state.expandDueToChildren;
    const { unresolvedActions } = this.props.actionsContext!;

    return [
      <SidebarItem
        key="/"
        to="/"
        label="Home"
        icon="Home"
        selected={location.pathname === '/'}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
      />,

      <SidebarItem
        key="/opportunities"
        to="/opportunities/activePending"
        label="New"
        icon="Funds"
        selected={location.pathname.startsWith('/opportunities')}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
      >
        <SidebarItem
          to="/opportunities/comingSoon"
          label="Coming Soon"
          selected={location.pathname === '/opportunities/comingSoon'}
          dense
        />
        <SidebarItem
          to="/opportunities/EOI"
          label="EOI"
          selected={location.pathname === '/opportunities/EOI'}
          dense
        />
        <SidebarItem
          to="/opportunities/applicationOpen"
          label="Application Open"
          selected={location.pathname === '/opportunities/applicationOpen'}
          dense
        />
      </SidebarItem>,
      <SidebarItem
        key="/actions"
        to="/actions"
        label="Actions"
        icon="Actions"
        selected={location.pathname.startsWith('/actions')}
        onHoverIntent={this.handleHoverIntent}
        badgeCount={
          (unresolvedActions(['investment-entity-association'])?.length || 0) +
          (unresolvedActions(['placeholder-contact'])?.length || 0)
        }
        notificationComponent={
          mobile ? (
            <NotificationBubble
              show={
                (unresolvedActions(['investment-entity-association'])?.length || 0) +
                  (unresolvedActions(['placeholder-contact'])?.length || 0) >
                0
              }
              count={
                (unresolvedActions(['investment-entity-association'])?.length || 0) +
                (unresolvedActions(['placeholder-contact'])?.length || 0)
              }
            />
          ) : undefined
        }
      />,
      <PermissionSidebarItem
        key="/articlesForApproval"
        permissionKey={Permissions.NewsArticleApproval}
        to="/articlesForApproval"
        label="Approve Articles"
        icon="News"
        selected={location.pathname.startsWith('/articles')}
        onHoverIntent={this.handleHoverIntent}
        badgeCount={unresolvedActions(['article-approval'])?.length}
        notificationComponent={
          mobile ? (
            <NotificationBubble
              show={(unresolvedActions(['article-approval'])?.length || 0) > 0}
              count={unresolvedActions(['article-approval'])?.length || 0}
            />
          ) : undefined
        }
      />,
      <PermissionSidebarItem
        key="/news"
        permissionKey={Permissions.NewsArticleCreation}
        to="/news"
        label="News"
        icon="News"
        selected={location.pathname.startsWith('/news')}
        onHoverIntent={this.handleHoverIntent}
      />,
      <SidebarItem
        key="/contacts"
        to="/contacts"
        label="Contacts"
        icon="Contact"
        selected={location.pathname.startsWith('/contacts')}
        onHoverIntent={this.handleHoverIntent}
      />,
      <SidebarItem
        key="/adminUsers"
        to="/adminUsers"
        label="Admins"
        icon="Admin"
        selected={location.pathname.startsWith('/adminUsers')}
        onHoverIntent={this.handleHoverIntent}
      />,
      <PermissionSidebarItem
        key="/globalConfig"
        permissionKey={Permissions.EditGlobalConfiguration}
        to="/globalConfig"
        label="Global Config"
        icon="Settings"
        selected={location.pathname.startsWith('/globalConfig')}
        onHoverIntent={this.handleHoverIntent}
      />,
      <SettingsSidebarItem
        key="/settings"
        expandDueToChildren={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
      />,
      <SidebarItem
        key="/activityLogs"
        to="/activityLogs"
        label="Activity Logs"
        icon="History"
        selected={location.pathname.startsWith('/activityLogs')}
        onHoverIntent={this.handleHoverIntent}
      />,
      <JobStatusSidebarItem
        key="/jobs"
        expandDueToChildren={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        selected={location.pathname.startsWith('/jobs')}
      />,
    ];
  }
}

export default function Sidebar({ tone = 'tone4', mobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(CommonLoggedInDataContext);
  const actionsContext = useActions();

  return (
    <UserSidebarBase
      location={location}
      navigate={navigate}
      context={context!}
      tone={tone}
      mobile={mobile}
      actionsContext={actionsContext}
    />
  );
}
